import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil, switchMap, of } from 'rxjs';
import { ConvivesService } from '../../../convives/convives.service';
import { GlobalSearchService } from '../../services/global-search.service';
import { AuthService } from '../../../auth/auth.service';
import { Convive } from '../../models/api.models';
import { getISOWeekNumber } from '../../utils/date.utils';
import { environment } from '../../../../environments/environment';

interface SearchResult {
  id: string;
  type: 'convive' | 'menu' | 'tournee' | 'action';
  title: string;
  subtitle?: string;
  icon: string;
  data?: any;
  actions?: SearchAction[];
}

interface SearchAction {
  label: string;
  icon: string;
  action: () => void;
  primary?: boolean;
}

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss'
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  searchQuery = '';
  isOpen = false;
  loading = false;
  results: SearchResult[] = [];
  selectedIndex = 0;

  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  constructor(
    private convivesService: ConvivesService,
    private globalSearchService: GlobalSearchService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit() {
    // Setup search with debounce
    this.searchSubject$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.trim().length < 2) {
          return of([]);
        }
        return this.performSearch(query);
      }),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      this.results = results;
      this.loading = false;
      this.selectedIndex = 0;
    });

    // Listen for keyboard shortcuts globally
    document.addEventListener('keydown', this.handleGlobalKeydown.bind(this));

    // Listen for service calls to open search
    this.globalSearchService.openSearch$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.openSearch());

    // Show quick actions on init
    this.results = this.getQuickActions();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('keydown', this.handleGlobalKeydown.bind(this));
  }

  private handleGlobalKeydown(event: KeyboardEvent) {
    // Cmd+K or Ctrl+K to open search
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.openSearch();
    }

    // Escape to close
    if (event.key === 'Escape' && this.isOpen) {
      this.closeSearch();
    }
  }

  openSearch() {
    this.isOpen = true;
    this.searchQuery = '';
    this.results = this.getQuickActions();
    this.selectedIndex = 0;
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    }, 100);
  }

  closeSearch() {
    this.isOpen = false;
    this.searchQuery = '';
    this.results = [];
    this.selectedIndex = 0;
  }

  onSearchInput(query: string) {
    this.searchQuery = query;
    
    if (query.trim().length < 2) {
      this.results = this.getQuickActions();
      this.loading = false;
      return;
    }

    this.loading = true;
    this.searchSubject$.next(query);
  }

  private performSearch(query: string): Subject<SearchResult[]> {
    const results$ = new Subject<SearchResult[]>();
    const allResults: SearchResult[] = [];

    // Search convives
    this.convivesService.getConvives({ search: query, limit: 5 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const conviveResults = response.data.map((convive: Convive) => this.mapConviveToResult(convive));
          allResults.push(...conviveResults);
          
          // Add quick actions if query matches
          const quickActions = this.getMatchingQuickActions(query);
          allResults.push(...quickActions);
          
          results$.next(allResults);
        },
        error: () => {
          results$.next(allResults);
        }
      });

    return results$;
  }

  private mapConviveToResult(convive: Convive): SearchResult {
    return {
      id: `convive-${convive.id}`,
      type: 'convive',
      title: convive.nomComplet,
      subtitle: `${convive.email || 'Pas d\'email'} • ${convive.tournee?.nom || 'Pas de tournée'}`,
      icon: '👤',
      data: convive,
      actions: [
        {
          label: 'Saisir commande',
          icon: '📝',
          action: () => this.navigateToNewOrder(convive),
          primary: true
        },
        {
          label: 'Modifier',
          icon: '✏️',
          action: () => this.navigateToEditConvive(convive)
        },
        {
          label: 'Voir détails',
          icon: '👁️',
          action: () => this.navigateToConviveDetails(convive)
        }
      ]
    };
  }

  private getQuickActions(): SearchResult[] {
    const actions: SearchResult[] = [
      {
        id: 'action-new-order',
        type: 'action',
        title: 'Nouvelle commande',
        subtitle: 'Créer une nouvelle commande pour un convive',
        icon: '➕',
        actions: [{
          label: 'Ouvrir',
          icon: '→',
          action: () => {
            const currentWeek = getISOWeekNumber();
            this.router.navigate(['/commandes'], {
              queryParams: { semaine: currentWeek }
            });
          },
          primary: true
        }]
      },
      {
        id: 'action-restes',
        type: 'action',
        title: 'Restes à saisir',
        subtitle: 'Voir les convives sans commande',
        icon: '⚠️',
        actions: [{
          label: 'Ouvrir',
          icon: '→',
          action: () => this.router.navigate(['/commandes/restes-a-saisir']),
          primary: true
        }]
      },
      {
        id: 'action-menus',
        type: 'action',
        title: 'Gérer les menus',
        subtitle: 'Consulter et modifier les menus hebdomadaires',
        icon: '📋',
        actions: [{
          label: 'Ouvrir',
          icon: '→',
          action: () => this.router.navigate(['/menus/calendar']),
          primary: true
        }]
      },
      {
        id: 'action-convives',
        type: 'action',
        title: 'Liste des convives',
        subtitle: 'Gérer la liste des convives',
        icon: '👥',
        actions: [{
          label: 'Ouvrir',
          icon: '→',
          action: () => this.router.navigate(['/convives']),
          primary: true
        }]
      },
      {
        id: 'action-new-convive',
        type: 'action',
        title: 'Nouveau convive',
        subtitle: 'Ajouter un nouveau convive à la base',
        icon: '👤',
        actions: [{
          label: 'Créer',
          icon: '✨',
          action: () => this.router.navigate(['/convives'], { queryParams: { action: 'new' } }),
          primary: true
        }]
      },
      {
        id: 'action-tournees',
        type: 'action',
        title: 'Gérer les tournées',
        subtitle: 'Consulter et modifier les tournées',
        icon: '🚗',
        actions: [{
          label: 'Ouvrir',
          icon: '→',
          action: () => this.router.navigate(['/convives'], { queryParams: { action: 'tournees' } }),
          primary: true
        }]
      },
      {
        id: 'action-rapports',
        type: 'action',
        title: 'Rapports et statistiques',
        subtitle: 'Consulter les rapports et statistiques',
        icon: '📊',
        actions: [{
          label: 'Ouvrir',
          icon: '→',
          action: () => this.router.navigate(['/reports']),
          primary: true
        }]
      },
      {
        id: 'action-export',
        type: 'action',
        title: 'Exporter les commandes',
        subtitle: 'Télécharger un export CSV des commandes',
        icon: '📥',
        actions: [{
          label: 'Ouvrir',
          icon: '→',
          action: () => this.router.navigate(['/reports/export-commandes']),
          primary: true
        }]
      },
      {
        id: 'action-dashboard',
        type: 'action',
        title: 'Tableau de bord',
        subtitle: 'Retour à l\'accueil',
        icon: '🏠',
        actions: [{
          label: 'Ouvrir',
          icon: '→',
          action: () => this.router.navigate(['/']),
          primary: true
        }]
      }
    ];

    // Ajouter l'action admin si l'utilisateur est admin
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'super_admin')) {
      // Utiliser l'URL du backend pour accéder à l'admin
      const adminUrl = `${environment.apiUrl}/admin`;
      actions.push({
        id: 'action-admin',
        type: 'action',
        title: 'Administration',
        subtitle: 'Accéder au panel d\'administration',
        icon: '⚙️',
        actions: [{
          label: 'Ouvrir',
          icon: '→',
          action: () => {
            window.location.href = adminUrl;
          },
          primary: true
        }]
      });
    }

    return actions;
  }

  private getMatchingQuickActions(query: string): SearchResult[] {
    const quickActions = this.getQuickActions();
    const lowerQuery = query.toLowerCase();
    
    return quickActions.filter(action => 
      action.title.toLowerCase().includes(lowerQuery) ||
      action.subtitle?.toLowerCase().includes(lowerQuery)
    );
  }

  onKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.results.length - 1);
        this.scrollToSelected();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.scrollToSelected();
        break;
      case 'Enter':
        event.preventDefault();
        this.selectResult(this.results[this.selectedIndex]);
        break;
    }
  }

  private scrollToSelected() {
    setTimeout(() => {
      const selected = document.querySelector('.search-result-selected');
      selected?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }

  selectResult(result: SearchResult) {
    if (!result) return;

    const primaryAction = result.actions?.find(a => a.primary) || result.actions?.[0];
    if (primaryAction) {
      primaryAction.action();
      this.closeSearch();
    }
  }

  executeAction(action: SearchAction, event: Event) {
    event.stopPropagation();
    action.action();
    this.closeSearch();
  }

  private navigateToNewOrder(convive: Convive) {
    const currentWeek = getISOWeekNumber();
    this.router.navigate(['/commandes'], { 
      queryParams: { 
        conviveId: convive.id,
        semaine: currentWeek
      } 
    });
  }

  private navigateToEditConvive(convive: Convive) {
    this.router.navigate(['/convives'], { 
      queryParams: { edit: convive.id } 
    });
  }

  private navigateToConviveDetails(convive: Convive) {
    this.router.navigate(['/convives'], { 
      queryParams: { view: convive.id } 
    });
  }

  getResultIcon(result: SearchResult): string {
    return result.icon;
  }

  onBackdropClick() {
    this.closeSearch();
  }
}
